import React from "react";
import { Link, Route } from "react-router-dom";

import NotFound from "../components/NotFound";
import RequireAuth from "../components/RequireAuth";
import { AUTH_LEVELS } from "../lib/globals";
import { loader as ClubhouseTeamLoader } from "../loaders/ClubhouseTeamLoader";
import ClubhouseCOI, {
    loader as ClubhouseCOILoader,
} from "../pages/Clubhouse/COI";
import ClubhouseIndex, {
    loader as ClubhouseLoader,
} from "../pages/Clubhouse/Index";
import ClubhouseLayout from "../pages/Clubhouse/Layout";
import Player, { loader as PlayerLoader } from "../pages/Clubhouse/Player";
import Ratings, { loader as RatingsLoader } from "../pages/Clubhouse/Ratings";
import TournamentRegistrations, {
    loader as RegistrationsLoader,
} from "../pages/Clubhouse/Registrations";
import Roster, { loader as RosterLoader } from "../pages/Clubhouse/Roster";
import ClubhouseTeam from "../pages/Clubhouse/Team";
import ClubhouseTeamSchedule, {
    loader as ClubhouseTeamScheduleLoader,
} from "../pages/Clubhouse/TeamSchedule";
import Rules, { loader as RulesLoader } from "../pages/Index/Rules";
import GameLogs, {
    loader as GameLogsLoader,
} from "../pages/TournamentManagement/GameLogs";
import { IGame, IPlayer, ITeam } from "../types/api";

export default (
    <Route element={<RequireAuth allowedUserLevels={AUTH_LEVELS.clubhouse} />}>
        <Route
            path="coachs-clubhouse"
            handle={{
                crumb: () => (
                    <Link to="/coachs-clubhouse">Coach's Clubhouse</Link>
                ),
            }}
            element={<ClubhouseLayout />}
        >
            <Route
                index
                element={<ClubhouseIndex />}
                loader={ClubhouseLoader}
                handle={{
                    header: () => "Coach's Clubhouse",
                }}
            />
            <Route path="team" id="clubhouseTeam">
                <Route
                    path=":teamId"
                    element={<ClubhouseTeam />}
                    loader={ClubhouseTeamLoader}
                    shouldRevalidate={() => true}
                    handle={{
                        crumb: ({ teamId }: { teamId: string }) => {
                            return (
                                <Link to={`/coachs-clubhouse/team/${teamId}`}>
                                    Team
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                >
                    <Route
                        path="coi"
                        element={<ClubhouseCOI />}
                        loader={ClubhouseCOILoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/coi`}
                                    >
                                        Team Certificate
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="ratings"
                        element={<Ratings />}
                        loader={RatingsLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/ratings`}
                                    >
                                        Team Ratings
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="schedule"
                        element={<ClubhouseTeamSchedule />}
                        loader={ClubhouseTeamScheduleLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/schedule`}
                                    >
                                        Team Schedule
                                    </Link>
                                );
                            },
                        }}
                    >
                        <Route
                            path="game/:gameId/logs"
                            element={
                                <RequireAuth
                                    allowedUserLevels={
                                        AUTH_LEVELS.tourneymanagement
                                    }
                                />
                            }
                        >
                            <Route
                                index
                                element={<GameLogs />}
                                loader={GameLogsLoader}
                                handle={{
                                    crumb: ({
                                        teamId,
                                        gameId,
                                    }: {
                                        teamId: ITeam["id"];
                                        gameId: IGame["id"];
                                    }) => {
                                        return (
                                            <Link
                                                to={`/coachs-clubhouse/teams/${teamId}/game/logs/${gameId}`}
                                            >
                                                Game Logs
                                            </Link>
                                        );
                                    },
                                }}
                            />
                        </Route>
                    </Route>
                    <Route
                        path="roster"
                        element={<Roster />}
                        loader={RosterLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/roster`}
                                    >
                                        Team Roster
                                    </Link>
                                );
                            },
                        }}
                    >
                        <Route
                            path="edit/:playerId"
                            element={<Player />}
                            loader={PlayerLoader}
                            handle={{
                                crumb: ({
                                    teamId,
                                    player,
                                }: {
                                    teamId: ITeam["id"];
                                    player: IPlayer;
                                }) => {
                                    return (
                                        <Link
                                            to={`/coachs-clubhouse/team/${teamId}/roster/edit/${player.id}`}
                                        >
                                            Edit Player
                                        </Link>
                                    );
                                },
                            }}
                        />
                        <Route
                            path="add"
                            element={<Player />}
                            loader={PlayerLoader}
                            handle={{
                                crumb: ({
                                    teamId,
                                }: {
                                    teamId: ITeam["id"];
                                }) => {
                                    return (
                                        <Link
                                            to={`/coachs-clubhouse/team/${teamId}/roster/add`}
                                        >
                                            Add Player
                                        </Link>
                                    );
                                },
                            }}
                        />
                    </Route>
                    <Route
                        path="registrations"
                        element={<TournamentRegistrations />}
                        loader={RegistrationsLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/registrations`}
                                    >
                                        Tournament Registrations
                                    </Link>
                                );
                            },
                        }}
                    />
                </Route>
            </Route>
            <Route
                path="rules"
                element={<Rules />}
                loader={params =>
                    RulesLoader({
                        ...params,
                        content: "wibb_rules",
                    })
                }
                handle={{
                    crumb: () => (
                        <Link to="/coachs-clubhouse/rules">
                            MAJOR YBL Rules
                        </Link>
                    ),
                    header: () => "Wisconsin Baseball Tournaments Rules",
                }}
            />
        </Route>
        <Route
            path="*"
            element={<NotFound parent="clubhouse" />}
            handle={{ header: () => "Clubhouse Page Not Found" }}
        />
    </Route>
);
